import { useState, useEffect } from 'react'
import { message } from 'antd'

const useExercisesManagement = () => {
  const [loading, setLoading] = useState(false)
  const [exercises, setExercises] = useState([])

  useEffect(() => {
    const fetchExercises = async () => {
      setLoading(true)
      try {
        // Simulate API call to fetch exercises from the server/backend
        await new Promise((resolve) => setTimeout(resolve, 800))

        // Normally we would fetch from an API
        // For now, we'll use the exercises from the local mock data
        let response = []
        try {
          const module = await import('../utils/exercises.json')
          if (module && module.default) {
            response = module.default
          } else {
            console.error('Error loading exercises.json: Module or default export not found')
            message.error('Failed to load exercises data')
          }
        } catch (importError) {
          console.error('Error importing exercises.json:', importError)
          message.error('Failed to load exercises data')
        }

        setExercises(response)
      } catch (error) {
        console.error('Error fetching rehabilitation exercises:', error)
        message.error('Failed to load exercises')
      } finally {
        setLoading(false)
      }
    }

    fetchExercises()
  }, [])

  const addExercise = async (exerciseData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const newExercise = {
        id: `exercise-${Date.now()}`,
        ...exerciseData
      }

      setExercises([...exercises, newExercise])
      return newExercise
    } catch (error) {
      console.error('Error adding exercise:', error)
      message.error('Failed to add exercise')
      return null
    } finally {
      setLoading(false)
    }
  }

  const updateExercise = async (exerciseId, exerciseData) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 600))

      const updatedExercises = exercises.map((exercise) => {
        if (exercise.id === exerciseId) {
          return { ...exercise, ...exerciseData }
        }
        return exercise
      })

      setExercises(updatedExercises)
      return true
    } catch (error) {
      console.error('Error updating exercise:', error)
      message.error('Failed to update exercise')
      return false
    } finally {
      setLoading(false)
    }
  }

  const deleteExercise = async (exerciseId) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 500))

      const updatedExercises = exercises.filter((exercise) => exercise.id !== exerciseId)
      setExercises(updatedExercises)
      return true
    } catch (error) {
      console.error('Error deleting exercise:', error)
      message.error('Failed to delete exercise')
      return false
    } finally {
      setLoading(false)
    }
  }

  const updateAdherence = async (exerciseId, adherenceValue) => {
    setLoading(true)
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 400))

      const updatedExercises = exercises.map((exercise) => {
        if (exercise.id === exerciseId) {
          return { ...exercise, adherence: adherenceValue }
        }
        return exercise
      })

      setExercises(updatedExercises)
      return true
    } catch (error) {
      console.error('Error updating exercise adherence:', error)
      message.error('Failed to update exercise adherence')
      return false
    } finally {
      setLoading(false)
    }
  }

  return {
    loading,
    exercises,
    addExercise,
    updateExercise,
    deleteExercise,
    updateAdherence
  }
}

export default useExercisesManagement
